import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  CheckCircle2,
  TrendingDown,
  Zap,
  Calculator,
  Ruler,
  Home as HomeIcon,
  Clock,
  Shield,
  Star,
  ChevronDown,
  AlertTriangle,
  Award,
  ArrowRight,
  Users,
} from "lucide-react";
import { IMAGES } from "@/assets/images";

// ─── Animation helpers ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

// ─── Countdown timer ─────────────────────────────────────────────────────────
function useCountdown(targetHours = 47) {
  const end = useRef(Date.now() + targetHours * 3600 * 1000);
  const [time, setTime] = useState({ h: targetHours, m: 59, s: 59 });
  useEffect(() => {
    const id = setInterval(() => {
      const diff = Math.max(0, end.current - Date.now());
      setTime({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ value, label }: { value: string; label: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <motion.div
      ref={ref}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      className="flex flex-col items-center gap-1 p-6 rounded-xl border border-border bg-card"
    >
      <span className="font-mono text-4xl font-bold text-primary">{value}</span>
      <span className="text-sm text-muted-foreground text-center">{label}</span>
    </motion.div>
  );
}

// ─── Feature card ─────────────────────────────────────────────────────────────
function FeatureCard({
  icon: Icon,
  title,
  body,
  badge,
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  badge?: string;
}) {
  return (
    <motion.div
      variants={fadeUp}
      className="relative flex flex-col gap-4 p-6 rounded-xl border border-border bg-card"
      style={{ borderLeft: "4px solid var(--primary)" }}
    >
      {badge && (
        <span className="absolute top-4 right-4 text-xs font-mono font-semibold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon size={22} />
      </div>
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </motion.div>
  );
}

// ─── Section wrapper ─────────────────────────────────────────────────────────
function Section({
  id,
  children,
  className = "",
}: {
  id?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`w-full py-20 px-4 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

// ─── Step badge ───────────────────────────────────────────────────────────────
function StepBadge({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <motion.div variants={fadeUp} className="flex gap-4">
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-primary text-primary-foreground font-mono font-bold text-lg">
        {n}
      </div>
      <div className="flex flex-col gap-1">
        <h4 className="font-semibold text-foreground">{title}</h4>
        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
      </div>
    </motion.div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function Home() {
  const { h, m, s } = useCountdown(47);
  const [spotsLeft] = useState(6);

  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true });

  const credRef = useRef(null);
  const credInView = useInView(credRef, { once: true });

  const featRef = useRef(null);
  const featInView = useInView(featRef, { once: true });

  const processRef = useRef(null);
  const processInView = useInView(processRef, { once: true });

  const pricingRef = useRef(null);
  const pricingInView = useInView(pricingRef, { once: true });

  const finalRef = useRef(null);
  const finalInView = useInView(finalRef, { once: true });

  // Smooth scroll helper — avoids HashRouter conflict with href="#id"
  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── TOP ANNOUNCEMENT BAR ─────────────────────────────────────── */}
      <div className="w-full bg-primary text-primary-foreground py-2 px-4 text-center text-sm font-semibold flex items-center justify-center gap-2">
        <AlertTriangle size={14} />
        <span>
          🔥 FOUNDING AGENT OFFER: Only{" "}
          <strong>{spotsLeft} spots</strong> remaining at 50% off —{" "}
          {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")} left
        </span>
      </div>

      {/* ── NAVBAR ───────────────────────────────────────────────────── */}
      <nav className="w-full border-b border-border bg-background/95 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <img
            src={IMAGES.UNTITLED_20260319_062213_4}
            alt="Rise Media Hub"
            className="h-10 w-auto object-contain"
          />
          <div className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <button onClick={scrollTo("features")} className="hover:text-foreground transition-colors cursor-pointer bg-transparent border-0 p-0 font-[inherit] text-[inherit]">Features</button>
            <button onClick={scrollTo("app-preview")} className="hover:text-foreground transition-colors cursor-pointer bg-transparent border-0 p-0 font-[inherit] text-[inherit]">See The App</button>
            <button onClick={scrollTo("process")} className="hover:text-foreground transition-colors cursor-pointer bg-transparent border-0 p-0 font-[inherit] text-[inherit]">How It Works</button>
            <button onClick={scrollTo("pricing")} className="hover:text-foreground transition-colors cursor-pointer bg-transparent border-0 p-0 font-[inherit] text-[inherit]">Pricing</button>
          </div>
          <button
            onClick={scrollTo("pricing")}
            className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition-opacity cursor-pointer border-0"
          >
            Claim Your Spot <ArrowRight size={14} />
          </button>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 1 — HERO
      ════════════════════════════════════════════════════════════════*/}
      <Section id="hero" className="relative overflow-hidden">
        {/* Background accent */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, color-mix(in srgb, var(--primary) 8%, transparent), transparent)",
          }}
        />
        <motion.div
          ref={heroRef}
          variants={stagger}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          className="relative flex flex-col items-center text-center gap-6 max-w-4xl mx-auto"
        >
          {/* Eyebrow */}
          <motion.span
            variants={fadeUp}
            className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-primary border border-primary/30 bg-primary/10 px-4 py-1.5 rounded-full"
          >
            <TrendingDown size={12} /> Lead Conversion Is Down 19.4%
          </motion.span>

          {/* Headline */}
          <motion.h1
            variants={fadeUp}
            className="text-5xl md:text-7xl font-bold leading-tight tracking-tight"
            style={{ fontFamily: "Oswald, sans-serif" }}
          >
            Stop Losing Leads to an{" "}
            <span className="text-primary">Outdated Experience.</span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            variants={fadeUp}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed"
          >
            Real estate lead conversion is down{" "}
            <strong className="text-foreground">19.4%</strong>. Stand out with the{" "}
            <em className="text-primary font-semibold not-italic">"Ultimate Branded Closing Gift"</em>—a
            digital renovation ecosystem that keeps your brand in your client's hand for the next{" "}
            <strong className="text-foreground">7 years</strong>. Only{" "}
            <strong className="text-foreground">20 Founding Agent spots</strong> available at{" "}
            <strong className="text-primary">$98.50/year</strong> — fully branded &amp; live in{" "}
            <strong className="text-foreground">48 hours</strong>.
          </motion.p>

          {/* CTA */}
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 items-center">
            <button
              onClick={scrollTo("pricing")}
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold text-lg px-8 py-4 rounded-xl shadow-lg hover:opacity-90 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer border-0"
              style={{
                boxShadow: "0 8px 30px color-mix(in srgb, var(--primary) 35%, transparent)",
              }}
            >
              Secure Your Founding Agent License (50% Off)
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          <motion.p variants={fadeUp} className="text-xs text-muted-foreground">
            ✅ Only {spotsLeft} spots left &nbsp;|&nbsp; ✅ Live in 48 hours &nbsp;|&nbsp; ✅
            Fully branded with your logo
          </motion.p>

          {/* Hero product image */}
          <motion.div
            variants={fadeUp}
            className="w-full mt-6 rounded-2xl overflow-hidden border-2 shadow-2xl"
            style={{
              borderColor: "var(--primary)",
              boxShadow: "0 20px 60px color-mix(in srgb, var(--primary) 20%, transparent)",
            }}
          >
            <img
              src={IMAGES.RENO_PRO_VARIATION_3}
              alt="The Reno Pro — Founding Agent Offer: Only 20 Spots at 50% Off"
              className="w-full object-cover"
            />
          </motion.div>
        </motion.div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════════
          STATS BAND
      ════════════════════════════════════════════════════════════════*/}
      <div className="w-full border-y border-border bg-card py-10 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard value="89%" label="of buyers choose agent based on EXPERIENCE not price" />
          <StatCard value="19.4%" label="industry-wide drop in lead conversion" />
          <StatCard value="188%" label="ROI on Steel Entry Door Replacement" />
          <StatCard value="7 yrs" label="your brand stays in the client's hand" />
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 2 — CREDIBILITY GAP (SIDE-BY-SIDE COMPARISON)
      ════════════════════════════════════════════════════════════════*/}
      <Section id="credibility">
        <motion.div
          ref={credRef}
          variants={stagger}
          initial="hidden"
          animate={credInView ? "visible" : "hidden"}
          className="flex flex-col gap-12"
        >
          <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto">
            <h2
              className="text-4xl md:text-5xl font-bold mb-4 text-foreground"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              The Credibility Gap Is Costing You Deals
            </h2>
            <p className="text-muted-foreground text-lg">
              Research proves prospects find data on digital devices{" "}
              <strong className="text-foreground">significantly more credible</strong> than verbal or
              paper quotes.
            </p>
          </motion.div>

          {/* Side-by-side comparison */}
          <div className="grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-border">
            {/* LEFT — Old way */}
            <motion.div
              variants={fadeUp}
              className="relative flex flex-col gap-4 p-8 bg-muted/30"
            >
              <div className="absolute top-4 left-4 bg-destructive text-destructive-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                ❌ Old Way
              </div>
              <div className="mt-8 flex flex-col items-center justify-center gap-6 min-h-[280px]">
                {/* Napkin illustration */}
                <div
                  className="w-48 h-40 rounded-lg bg-card border-2 border-border flex flex-col items-start justify-start p-4 shadow-inner rotate-2"
                  style={{ fontFamily: "cursive", background: "#f5f0e8" }}
                >
                  <p style={{ color: "#333", fontSize: "11px", lineHeight: "1.6" }}>
                    Paint: ~$800?<br />
                    Flooring: idk $3k<br />
                    Kitchen: maybe $15k<br />
                    Total: ≈ $20k ish
                  </p>
                  <div className="mt-2 border-b border-gray-400 w-full" />
                  <p style={{ color: "#555", fontSize: "10px", marginTop: "6px" }}>
                    *rough estimate
                  </p>
                </div>
                <p className="text-center text-muted-foreground text-sm">
                  Scribbled napkin estimates. No data. No credibility.
                </p>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><AlertTriangle size={14} className="text-destructive flex-shrink-0" /> Verbal quotes feel unreliable</li>
                <li className="flex items-center gap-2"><AlertTriangle size={14} className="text-destructive flex-shrink-0" /> No professional brand presence</li>
                <li className="flex items-center gap-2"><AlertTriangle size={14} className="text-destructive flex-shrink-0" /> Client fear and long hesitation periods</li>
                <li className="flex items-center gap-2"><AlertTriangle size={14} className="text-destructive flex-shrink-0" /> Agent forgotten after closing</li>
              </ul>
            </motion.div>

            {/* RIGHT — New way */}
            <motion.div
              variants={fadeUp}
              className="relative flex flex-col gap-4 p-8 bg-card"
              style={{ borderLeft: "2px solid var(--primary)" }}
            >
              <div className="absolute top-4 left-4 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                ✅ The Reno Pro
              </div>
              <div className="mt-8 flex flex-col items-center justify-center gap-6 min-h-[280px]">
                <div className="relative w-48">
                  <img
                    src={IMAGES.RENO_PRO_BRAND_IDENTITY_FIXED_1_1}
                    alt="The Reno Pro Digital Command Center"
                    className="w-full rounded-xl border-2 shadow-xl"
                    style={{ borderColor: "var(--primary)" }}
                  />
                </div>
                <p className="text-center text-muted-foreground text-sm">
                  Precision digital ecosystem. Architectural formulas. Professional credibility.
                </p>
              </div>
              <ul className="space-y-2 text-sm text-foreground">
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary flex-shrink-0" /> Architectural-grade calculations on device</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary flex-shrink-0" /> Branded with your logo &amp; headshot</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary flex-shrink-0" /> Instant ROI clarity eliminates hesitation</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary flex-shrink-0" /> Keeps you top-of-mind for 7+ years</li>
              </ul>
            </motion.div>
          </div>
        </motion.div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 3 — CORE FEATURES
      ════════════════════════════════════════════════════════════════*/}
      <Section
        id="features"
        className="bg-gradient-to-b from-background to-card"
      >
        <motion.div
          ref={featRef}
          variants={stagger}
          initial="hidden"
          animate={featInView ? "visible" : "hidden"}
          className="flex flex-col gap-12"
        >
          <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              Core High-Utility Features
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold mt-2 text-foreground"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              One System. Zero Chaos.
            </h2>
            <p className="mt-3 text-muted-foreground text-lg">
              Replace scattered spreadsheets and sloppy paper notes with a professional digital suite
              your clients will use every day.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={Calculator}
              title="ROI & ARV Calculator"
              badge="188% ROI"
              body="Integrated 70% Rule math helps clients visualize equity growth instantly. Show them exact returns on Steel Entry Doors (188%), Patio Doors (145%), and kitchen refreshes (96%)."
            />
            <FeatureCard
              icon={Ruler}
              title="Precision Material Estimators"
              badge="10–15% factor"
              body="Architectural formulas for paint and flooring including a professional 10–15% waste factor to prevent client 'budget leaks' and avoid embarrassing cost overruns."
            />
            <FeatureCard
              icon={HomeIcon}
              title="No-Demo Kitchen Guides"
              badge="96% ROI"
              body="High-value strategies for 96% ROI kitchen refreshes that can transform a space in just 3–5 days without costly demolition — the new client favorite."
            />
            <FeatureCard
              icon={Shield}
              title="Wealth Management Vault"
              body="Digital storage for Fixed Home Assets, maintenance schedules, and insurance documentation. Protects your client's largest investment long after closing."
            />
            <FeatureCard
              icon={Clock}
              title="7-Year Rule Logic"
              body="Strategic occupancy guidance: staying &lt;7 years? Focus on high-ROI resale projects. Staying longer? Prioritize custom lifestyle upgrades. You become their trusted advisor."
            />
            <FeatureCard
              icon={Zap}
              title="Visual Design AI"
              body="Clients upload photos of their new space to visualize designs before spending a dime. Eliminates fear of the unknown and accelerates purchase decisions."
            />
          </div>

          {/* Showcase image */}
          <motion.div variants={fadeUp} className="rounded-2xl overflow-hidden border border-border">
            <img
              src={IMAGES.LUXURY_HOME_KITCHEN_MODERN_RENOVATION_BE_6}
              alt="Modern luxury kitchen renovation"
              className="w-full h-72 object-cover"
            />
          </motion.div>
        </motion.div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 3b — SEE IT IN ACTION (APP SCREENSHOTS)
      ════════════════════════════════════════════════════════════════*/}
      <Section id="app-preview" className="bg-card">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex flex-col gap-14"
        >
          {/* Section header */}
          <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              Real App. Real Screens.
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold mt-2 text-foreground"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              Your Clients Open This App —{" "}
              <span className="text-primary">Not a Folder of Printouts.</span>
            </h2>
            <p className="mt-3 text-muted-foreground text-lg">
              A fully live web &amp; mobile app — works on iPhone, Android, and desktop.
              No download required. Here's what your clients actually see.
            </p>
            {/* Device badges */}
            <div className="flex items-center justify-center gap-3 mt-5 flex-wrap">
              {[
                { icon: "📱", label: "iPhone & Android" },
                { icon: "💻", label: "Desktop" },
                { icon: "📲", label: "No App Download" },
                { icon: "⚡", label: "Instant Access" },
              ].map((b) => (
                <span
                  key={b.label}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary"
                >
                  {b.icon} {b.label}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Screenshot 1 — Mobile ARV Dashboard (full-width hero treatment) */}
          <motion.div
            variants={fadeUp}
            className="grid md:grid-cols-2 gap-10 items-center"
          >
            {/* Phone mockup frame */}
            <div className="flex justify-center">
              <div
                className="relative rounded-[2.5rem] border-[6px] border-foreground shadow-2xl overflow-hidden"
                style={{
                  maxWidth: 300,
                  boxShadow: "0 30px 80px color-mix(in srgb, var(--primary) 25%, transparent), 0 8px 30px rgba(0,0,0,0.3)",
                }}
              >
                {/* Phone notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-foreground rounded-b-2xl z-10" />
                <img
                  src={IMAGES.SCREENSHOT_ARV_DASHBOARD}
                  alt="Reno Pro mobile app — ARV Calculator and daily checklist"
                  className="w-full object-cover"
                />
              </div>
            </div>
            {/* Copy */}
            <div className="flex flex-col gap-5">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                <span className="w-6 h-0.5 bg-primary" /> Screen 1 of 5 — Mobile App
              </div>
              <h3
                className="text-3xl md:text-4xl font-bold text-foreground"
                style={{ fontFamily: "Oswald, sans-serif" }}
              >
                Live ARV Calculator &amp; Daily Project Dashboard
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Your client opens their phone and sees{" "}
                <strong className="text-foreground">their agent's branding</strong> front and
                center — your logo, your name, your "Contact Agent" button. Below it, a{" "}
                <strong className="text-foreground">live equity tracker</strong> showing their
                estimated After Repair Value ($451,460 in this example), today's task checklist,
                and real-time budget alerts. This is the tool they check{" "}
                <strong className="text-primary">every single day.</strong>
              </p>
              <div className="flex flex-col gap-2">
                {[
                  "Your photo & brokerage logo on every screen",
                  "Live ARV — shows clients real equity growth",
                  "Daily task checklist keeps projects on track",
                  "Budget alerts stop cost overruns before they happen",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 size={15} className="text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Screenshot 2 — Branded Desktop: Header & Footer */}
          <motion.div variants={fadeUp} className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                <span className="w-6 h-0.5 bg-primary" /> Screen 2 of 5 — Your Brand on Every Page
              </div>
              <h3
                className="text-3xl md:text-4xl font-bold text-foreground"
                style={{ fontFamily: "Oswald, sans-serif" }}
              >
                Your Logo, Photo &amp; Contact Details —{" "}
                <span className="text-primary">Header &amp; Footer, Every Screen.</span>
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Every page your client opens shows{" "}
                <strong className="text-foreground">your brokerage logo, headshot, phone number</strong>{" "}
                and a <strong className="text-primary">"Contact Agent"</strong> button locked in the
                header — so your brand is the first and last thing they see. The footer carries your
                full agent card: photo, name, tagline, email, website, listings link, and social profiles.
                This is passive marketing that runs{" "}
                <strong className="text-foreground">every time they open the app.</strong>
              </p>
            </div>

            {/* Two branded screenshots stacked */}
            <div className="flex flex-col gap-4">
              {/* Branded header screenshot */}
              <div
                className="w-full rounded-xl overflow-hidden border border-border shadow-xl"
                style={{
                  boxShadow: "0 12px 40px color-mix(in srgb, var(--primary) 15%, transparent)",
                }}
              >
                <div className="flex items-center gap-1.5 px-3 py-2 bg-muted border-b border-border">
                  <span className="w-3 h-3 rounded-full bg-destructive/60" />
                  <span className="w-3 h-3 rounded-full bg-primary/60" />
                  <span className="w-3 h-3 rounded-full bg-green-500/60" />
                  <div className="flex-1 mx-3 bg-background rounded px-3 py-0.5 text-xs text-muted-foreground font-mono truncate">
                    app.risemediahub.com — Branded Header &amp; Budget Dashboard
                  </div>
                </div>
                <img
                  src={IMAGES.SCREENSHOT_BRANDED_HEADER}
                  alt="Branded desktop app — agent logo and Contact Agent button in header, live budget tracker"
                  className="w-full object-cover"
                />
              </div>

              {/* Callout label between the two */}
              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-dashed border-primary/30" />
                <span className="text-xs font-semibold text-primary uppercase tracking-widest whitespace-nowrap px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
                  ↓ Branded Footer — Agent Card on Every Page
                </span>
                <div className="flex-1 border-t border-dashed border-primary/30" />
              </div>

              {/* Branded footer screenshot */}
              <div
                className="w-full rounded-xl overflow-hidden border border-border shadow-xl"
                style={{
                  boxShadow: "0 12px 40px color-mix(in srgb, var(--primary) 15%, transparent)",
                }}
              >
                <div className="flex items-center gap-1.5 px-3 py-2 bg-muted border-b border-border">
                  <span className="w-3 h-3 rounded-full bg-destructive/60" />
                  <span className="w-3 h-3 rounded-full bg-primary/60" />
                  <span className="w-3 h-3 rounded-full bg-green-500/60" />
                  <div className="flex-1 mx-3 bg-background rounded px-3 py-0.5 text-xs text-muted-foreground font-mono truncate">
                    app.risemediahub.com — Branded Footer with Agent Card &amp; Social Links
                  </div>
                </div>
                <img
                  src={IMAGES.SCREENSHOT_BRANDED_FOOTER}
                  alt="Branded desktop app — agent card footer with photo, contact info, schedule consultation, view listings, website and social links"
                  className="w-full object-cover"
                />
              </div>
            </div>

            {/* Feature callouts */}
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              {[
                { icon: "🏷️", title: "Brokerage Logo", body: "Your logo in the header on every single page" },
                { icon: "📞", title: "Contact Button", body: "One-tap call-to-action locked in the top-right corner" },
                { icon: "🔗", title: "Listings & Website", body: "Direct links to your active listings and website" },
                { icon: "📲", title: "Socials in Footer", body: "Your social media profiles on every page of the app" },
              ].map((f) => (
                <div
                  key={f.title}
                  className="flex flex-col gap-2 p-4 rounded-xl border border-border bg-background"
                >
                  <span className="text-xl">{f.icon}</span>
                  <p className="text-sm font-semibold text-foreground">{f.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Screenshot 3 — Desktop Project Notes (reversed layout) */}
          <motion.div
            variants={fadeUp}
            className="grid md:grid-cols-2 gap-10 items-center"
          >
            {/* Copy — left */}
            <div className="flex flex-col gap-5 md:order-1 order-2">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                <span className="w-6 h-0.5 bg-primary" /> Screen 3 of 5 — Desktop App
              </div>
              <h3
                className="text-3xl md:text-4xl font-bold text-foreground"
                style={{ fontFamily: "Oswald, sans-serif" }}
              >
                Project Notes — Organised, Searchable, Always Synced
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                No more sticky notes or lost text threads. The app's{" "}
                <strong className="text-foreground">Project Notes</strong> section lets clients
                capture every contractor quote, material decision, and idea — tagged by project and
                searchable instantly. Works on{" "}
                <strong className="text-primary">desktop and mobile,</strong> so nothing falls
                through the cracks. You become the agent who gave them an organised system that
                works.
              </p>
              <div className="flex flex-col gap-2">
                {[
                  "Full desktop web app — no install needed",
                  "Notes organised by project with search",
                  "Print notes directly from the app",
                  "Rise Media Hub branding + your agent identity",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 size={15} className="text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Desktop browser mockup frame — right */}
            <div className="flex justify-center md:order-2 order-1">
              <div
                className="w-full rounded-xl overflow-hidden border border-border shadow-2xl"
                style={{
                  boxShadow: "0 20px 60px color-mix(in srgb, var(--primary) 15%, transparent), 0 4px 20px rgba(0,0,0,0.15)",
                }}
              >
                {/* Browser chrome bar */}
                <div className="flex items-center gap-1.5 px-3 py-2 bg-muted border-b border-border">
                  <span className="w-3 h-3 rounded-full bg-destructive/60" />
                  <span className="w-3 h-3 rounded-full bg-primary/60" />
                  <span className="w-3 h-3 rounded-full bg-green-500/60" />
                  <div className="flex-1 mx-3 bg-background rounded px-3 py-0.5 text-xs text-muted-foreground font-mono truncate">
                    app.risemediahub.com/notes
                  </div>
                </div>
                <img
                  src={IMAGES.SCREENSHOT_PROJECT_NOTES}
                  alt="Reno Pro desktop app — Project Notes screen"
                  className="w-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Screenshot 3 — Planner Home / Landing screen */}
          <motion.div
            variants={fadeUp}
            className="grid md:grid-cols-2 gap-10 items-center"
          >
            {/* Desktop browser mockup frame — left */}
            <div className="flex justify-center">
              <div
                className="w-full rounded-xl overflow-hidden border border-border shadow-2xl"
                style={{
                  boxShadow: "0 20px 60px color-mix(in srgb, var(--primary) 15%, transparent), 0 4px 20px rgba(0,0,0,0.15)",
                }}
              >
                {/* Browser chrome bar */}
                <div className="flex items-center gap-1.5 px-3 py-2 bg-muted border-b border-border">
                  <span className="w-3 h-3 rounded-full bg-destructive/60" />
                  <span className="w-3 h-3 rounded-full bg-primary/60" />
                  <span className="w-3 h-3 rounded-full bg-green-500/60" />
                  <div className="flex-1 mx-3 bg-background rounded px-3 py-0.5 text-xs text-muted-foreground font-mono truncate">
                    app.risemediahub.com
                  </div>
                </div>
                <img
                  src={IMAGES.SCREENSHOT_PLANNER_HOME}
                  alt="Reno Pro — Renovation Project Planner home screen with 85.7% average ROI"
                  className="w-full object-cover"
                />
              </div>
            </div>
            {/* Copy */}
            <div className="flex flex-col gap-5">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                <span className="w-6 h-0.5 bg-primary" /> Screen 4 of 5 — Planner Dashboard
              </div>
              <h3
                className="text-3xl md:text-4xl font-bold text-foreground"
                style={{ fontFamily: "Oswald, sans-serif" }}
              >
                The 30-Day Renovation Planner — Built on Real Data
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                The app's home screen greets clients with a{" "}
                <strong className="text-foreground">structured 30-day renovation timeline</strong>{" "}
                integrated with real-time financial and real estate intelligence. The{" "}
                <strong className="text-primary">85.7% average ROI</strong> badge isn't a
                marketing claim — it's live data pulled directly inside the tool. Your clients
                don't just feel confident. They have the numbers to prove it.
              </p>
              <div className="flex flex-col gap-2">
                {[
                  "30-day structured renovation timeline built-in",
                  "Real-time ROI data — avg. 85.7% across all projects",
                  "One-click 'Get Started' for new renovation projects",
                  "Professional UI your clients will be proud to show friends",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 size={15} className="text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Bottom CTA pull-through */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col items-center gap-4 p-8 rounded-2xl text-center"
            style={{
              background: "color-mix(in srgb, var(--primary) 8%, transparent)",
              border: "1px solid color-mix(in srgb, var(--primary) 25%, transparent)",
            }}
          >
            <p className="text-lg font-semibold text-foreground max-w-xl">
              This is the app your clients gift to friends. That's how referrals happen on autopilot.
            </p>
            <button
              onClick={scrollTo("pricing")}
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold text-base px-7 py-3.5 rounded-xl hover:opacity-90 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer border-0"
              style={{ boxShadow: "0 8px 24px color-mix(in srgb, var(--primary) 35%, transparent)" }}
            >
              Get Your Branded App — 50% Off
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-xs text-muted-foreground">
              ✅ Live in 48 hours &nbsp;|&nbsp; ✅ Works on all devices &nbsp;|&nbsp; ✅ No app store download needed
            </p>
          </motion.div>
        </motion.div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 4 — THE "MAGIC CHECKOUT" PROCESS
      ════════════════════════════════════════════════════════════════*/}
      <Section id="process" className="bg-card">
        <motion.div
          ref={processRef}
          variants={stagger}
          initial="hidden"
          animate={processInView ? "visible" : "hidden"}
          className="flex flex-col gap-12"
        >
          <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              The Magic Checkout
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold mt-2 text-foreground"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              Your Branded Ecosystem, Live in{" "}
              <span className="text-primary">48 Hours</span>
            </h2>
            <p className="mt-3 text-muted-foreground text-lg">
              Our automated onboarding collects everything we need at the point of purchase — no back-and-forth, no delays.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col gap-8">
              <StepBadge
                n={1}
                title="Secure Your License"
                body="Click the CTA and complete checkout in under 3 minutes. Your spot is locked the moment you pay."
              />
              <StepBadge
                n={2}
                title="Automated Brand Collection"
                body="Our onboarding form immediately collects your professional headshot, brokerage logo, and brand HEX codes — zero back-and-forth email chains."
              />
              <StepBadge
                n={3}
                title="We Build Your Ecosystem"
                body="Our team configures your fully branded digital renovation planner with your identity applied to every screen and calculator."
              />
              <StepBadge
                n={4}
                title="Your Ecosystem Goes Live"
                body="Within 48 hours, your custom-branded Reno Pro is live and ready to gift to clients at every closing. Start converting referrals immediately."
              />
            </div>

            <motion.div
              variants={fadeUp}
              className="flex flex-col gap-6 p-8 rounded-2xl border border-primary/30 bg-primary/5"
              style={{ boxShadow: "0 0 40px color-mix(in srgb, var(--primary) 8%, transparent)" }}
            >
              <div className="flex items-center gap-3">
                <Zap size={22} className="text-primary" />
                <h3 className="font-semibold text-lg text-foreground">What Gets Branded</h3>
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {[
                  "Professional headshot on every screen",
                  "Your brokerage logo prominently displayed",
                  "Custom HEX color palette throughout",
                  "Your contact info &amp; website URL",
                  "Your social media links",
                  "Custom welcome message to clients",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-primary flex-shrink-0 mt-0.5" />
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
              <div className="border-t border-border pt-4">
                <p className="text-xs text-muted-foreground italic">
                  "Stop giving wine that's gone in a night. Give a gift that keeps your face in your
                  client's hand for the next 7 years."
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 5 — SOCIAL PROOF
      ════════════════════════════════════════════════════════════════*/}
      <Section id="social-proof">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex flex-col gap-12"
        >
          <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              Experience Insurance
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold mt-2 text-foreground"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              Why Agents Are Switching
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote:
                  "I used to hand clients a folder of printouts. Now I hand them a branded digital ecosystem with their name on it. The look on their face is priceless.",
                name: "Marcus R.",
                role: "Senior Listing Agent, Dallas TX",
                stars: 5,
              },
              {
                quote:
                  "One referral from the planner paid for 5 years of the license. The ROI math isn't hard. I'm never going back to bottles of wine.",
                name: "Danielle K.",
                role: "Buyer's Agent, Phoenix AZ",
                stars: 5,
              },
              {
                quote:
                  "My clients actually call ME when they're ready for their next project because the planner keeps me in front of them constantly. Lead gen on autopilot.",
                name: "Jerome T.",
                role: "Team Lead, Atlanta GA",
                stars: 5,
              },
            ].map((t) => (
              <motion.div
                key={t.name}
                variants={fadeUp}
                className="flex flex-col gap-4 p-6 rounded-xl border border-border bg-card"
              >
                <div className="flex gap-1">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <Star key={i} size={14} className="fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed italic">
                  "{t.quote}"
                </p>
                <div className="mt-auto border-t border-border pt-3">
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Rise Media Hub brand image */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col md:flex-row gap-8 items-center p-8 rounded-2xl border border-border bg-card"
          >
            <img
              src={IMAGES.IMG_2_RISE_MEDIA_HUB_3}
              alt="Rise Media Hub"
              className="w-full md:w-1/2 rounded-xl object-cover"
            />
            <div className="flex flex-col gap-4">
              <h3 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Oswald, sans-serif" }}>
                Powered by Rise Media Hub
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                The Reno Pro is backed by the Rise Media Hub ecosystem — your all-in-one platform for
                building, scaling, and validating your online real estate business. Strategy, community,
                and tools that work.
              </p>
              <div className="flex flex-col gap-2">
                {[
                  "Start Small — Launch with confidence using proven frameworks",
                  "Validate Fast — Test ideas quickly, move to income without wasted effort",
                  "Scale Smart — Grow strategically with expert guidance at every stage",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 size={14} className="text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 6 — PRICING
      ════════════════════════════════════════════════════════════════*/}
      <Section id="pricing" className="bg-gradient-to-b from-card to-background">
        <motion.div
          ref={pricingRef}
          variants={stagger}
          initial="hidden"
          animate={pricingInView ? "visible" : "hidden"}
          className="flex flex-col gap-12"
        >
          <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              Founding Agent Offer
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold mt-2 text-foreground"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              Lock In Your{" "}
              <span className="text-primary">50% Discount</span> Before It's Gone
            </h2>
            <p className="mt-3 text-muted-foreground text-lg">
              Only <strong className="text-foreground">20 Founding Agent spots</strong> available.{" "}
              <strong className="text-destructive">{spotsLeft} remaining.</strong>
            </p>
          </motion.div>

          {/* Pricing cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto w-full">
            {/* Standard */}
            <motion.div
              variants={fadeUp}
              className="flex flex-col gap-6 p-8 rounded-2xl border border-border bg-card"
            >
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Standard Price</p>
                <div className="flex items-end gap-2 mt-2">
                  <span className="font-mono text-4xl font-bold text-muted-foreground line-through">$197</span>
                  <span className="text-muted-foreground text-sm">/year</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">+ $97 one-time setup fee</p>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {[
                  "Full Reno Pro ecosystem",
                  "Branded with your logo",
                  "All calculators & estimators",
                  "48-hour delivery",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-muted-foreground flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Founding Agent */}
            <motion.div
              variants={fadeUp}
              className="relative flex flex-col gap-6 p-8 rounded-2xl border-2 border-primary bg-card"
              style={{
                boxShadow: "0 0 50px color-mix(in srgb, var(--primary) 15%, transparent)",
              }}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider whitespace-nowrap">
                🔥 Founding Agent — {spotsLeft} Left
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-primary font-semibold">Founding Agent Price</p>
                <div className="flex items-end gap-2 mt-2">
                  <span className="font-mono text-5xl font-extrabold text-primary">$98.50</span>
                  <span className="text-muted-foreground text-sm">/year</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">+ $97 one-time setup fee</p>
                <span className="inline-block mt-2 text-xs bg-destructive/20 text-destructive font-semibold px-2 py-0.5 rounded">
                  Save $98.50/year — 50% off forever
                </span>
              </div>
              <ul className="space-y-2 text-sm text-foreground">
                {[
                  "Everything in Standard",
                  "Founding Agent badge &amp; priority support",
                  "Lock in 50% discount for life",
                  "$19 Contractor Agreement Pack add-on",
                  "Early access to new features",
                  "Your brand live in 48 hours",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-primary flex-shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: f }} />
                  </li>
                ))}
              </ul>
              <a
                href="https://pci.jotform.com/form/260681386227058"
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold text-lg px-8 py-4 rounded-xl hover:opacity-90 hover:-translate-y-0.5 transition-all duration-200"
                style={{
                  boxShadow: "0 8px 30px color-mix(in srgb, var(--primary) 35%, transparent)",
                }}
              >
                Secure Your Founding Agent License
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <p className="text-xs text-center text-muted-foreground">
                Secure checkout · 48-hour delivery · Cancel anytime
              </p>
            </motion.div>
          </div>

          {/* ROI math */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col md:flex-row gap-4 items-center justify-center text-center md:text-left p-6 rounded-xl border border-primary/20 bg-primary/5 max-w-3xl mx-auto w-full"
          >
            <Award size={32} className="text-primary flex-shrink-0" />
            <div>
              <p className="font-semibold text-foreground">
                The Math Is Simple: One Referral = 5+ Years of License Fees
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                At $98.50/year, one single referral generated by the professional experience this tool
                provides covers over five years of the license fee. It pays for itself.
              </p>
            </div>
          </motion.div>

          {/* Order bump */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col gap-4 p-6 rounded-xl border border-border bg-card max-w-3xl mx-auto w-full"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                <Users size={16} />
              </div>
              <h3 className="font-semibold text-foreground">Add-On: Contractor Agreement Template Pack — <span className="font-mono text-primary">$19</span></h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Protect your clients with legally sound contractor agreements. Solve "legal anxiety"
              at the exact moment they commit to renovation. Increases average order value by 30–50%.
            </p>
          </motion.div>
        </motion.div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 7 — FINAL CTA
      ════════════════════════════════════════════════════════════════*/}
      <Section
        id="cta"
        className="relative overflow-hidden"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 100%, color-mix(in srgb, var(--primary) 10%, transparent), transparent)",
          }}
        />
        <motion.div
          ref={finalRef}
          variants={stagger}
          initial="hidden"
          animate={finalInView ? "visible" : "hidden"}
          className="relative flex flex-col items-center text-center gap-8 max-w-2xl mx-auto"
        >
          <motion.div variants={fadeUp} className="flex flex-col items-center gap-2">
            <img
              src={IMAGES.UNTITLED_20260319_062213_4}
              alt="Rise Media Hub"
              className="h-12 w-auto object-contain mb-2"
            />
            <h2
              className="text-4xl md:text-5xl font-bold text-foreground"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              Don't Be the Agent Using{" "}
              <span className="text-primary">1990s Tech in a 2026 Market</span>
            </h2>
          </motion.div>

          <motion.p variants={fadeUp} className="text-lg text-muted-foreground">
            14 of 20 spots have already been claimed. Once these{" "}
            <strong className="text-foreground">{spotsLeft} remaining spots</strong> are gone, the
            price returns to $197/year — no exceptions.
          </motion.p>

          {/* Countdown */}
          <motion.div variants={fadeUp} className="flex gap-4">
            {[
              { val: String(h).padStart(2, "0"), label: "Hours" },
              { val: String(m).padStart(2, "0"), label: "Minutes" },
              { val: String(s).padStart(2, "0"), label: "Seconds" },
            ].map(({ val, label }) => (
              <div
                key={label}
                className="flex flex-col items-center gap-1 w-20 p-3 rounded-xl bg-card border border-border"
              >
                <span className="font-mono text-3xl font-bold text-primary">{val}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
              </div>
            ))}
          </motion.div>

          <motion.a
            variants={fadeUp}
            href="https://pci.jotform.com/form/260681386227058"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold text-xl px-10 py-5 rounded-xl hover:opacity-90 hover:-translate-y-1 transition-all duration-200"
            style={{
              boxShadow: "0 12px 40px color-mix(in srgb, var(--primary) 40%, transparent)",
            }}
          >
            Secure Your Founding Agent License — 50% Off
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </motion.a>

          <motion.p variants={fadeUp} className="text-xs text-muted-foreground">
            ✅ Only {spotsLeft} spots at $98.50/year &nbsp;|&nbsp; ✅ $97 one-time setup &nbsp;|&nbsp; ✅
            Live in 48 hours
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6 text-xs text-muted-foreground border-t border-border pt-6 w-full">
            <span className="flex items-center gap-1"><Shield size={12} className="text-primary" /> Secure Checkout</span>
            <span className="flex items-center gap-1"><Clock size={12} className="text-primary" /> 48-Hour Delivery</span>
            <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-primary" /> Fully Branded</span>
            <span className="flex items-center gap-1"><Award size={12} className="text-primary" /> Founding Agent Status</span>
          </motion.div>
        </motion.div>
      </Section>

      {/* ── FOOTER ───────────────────────────────────────────────────── */}
      <footer className="w-full border-t border-border bg-card py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <img
              src={IMAGES.UNTITLED_20260319_062213_4}
              alt="Rise Media Hub"
              className="h-7 w-auto object-contain"
            />
            <span>© 2026 Rise Media Hub. All rights reserved.</span>
          </div>
          <div className="flex gap-4">
            <button onClick={scrollTo("hero")} className="hover:text-foreground transition-colors cursor-pointer bg-transparent border-0 p-0">
              <ChevronDown className="rotate-180" size={14} />
            </button>
            <span>risemediahub.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
